// SPDX-License-Identifier: Elastic-2.0
/**
 * HomeGraph — interactive top-down floor plan of the home.
 *
 * Ports `ui_kits/dashboard/HomeGraph.jsx` from the design handoff.
 * v1 scope is the SVG floor plan alone — no detail panel (the
 * design's right-rail "Habitación · stats · CTAs"). Click selects a
 * room (visual highlight only); the detail panel is deferred to a
 * follow-up that needs real `/api/mirlai/v1/rooms/{id}` data anyway.
 *
 * Room positions / dimensions / pulse coordinates are hard-coded
 * fixtures matching the design's demo state. Once F-300 publishes a
 * home-graph endpoint (deferred F-3xx) those become props.
 *
 * Pulsing device dots use inline SVG `<animate>` rather than CSS
 * keyframes because individual ping positions need per-element
 * timing offsets — easier to keep them sibling-relative in the
 * SVG element than to generate N keyframe rules.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon, type IconName } from "../../components/Icon/Icon";

import styles from "./HomeGraph.module.css";

interface Ping {
  readonly x: number;
  readonly y: number;
}

interface Room {
  readonly id: string;
  readonly name: string;
  readonly icon: IconName;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly temp: number | null;
  readonly state: "on" | "off";
  readonly devices: number;
  readonly pings: readonly Ping[];
}

const FLOORPLAN = {
  width: 1100,
  height: 620,
  rooms: [
    { id: "living", name: "Living", icon: "music", x: 40, y: 40, w: 360, h: 280,
      temp: 21.4, state: "on", devices: 6,
      pings: [{ x: 90, y: 120 }, { x: 200, y: 80 }, { x: 300, y: 240 }, { x: 120, y: 240 }, { x: 340, y: 160 }, { x: 230, y: 180 }] },
    { id: "cocina", name: "Cocina", icon: "bulb", x: 420, y: 40, w: 280, h: 200,
      temp: 20.8, state: "on", devices: 5,
      pings: [{ x: 60, y: 100 }, { x: 160, y: 60 }, { x: 230, y: 140 }, { x: 100, y: 160 }, { x: 200, y: 90 }] },
    { id: "comedor", name: "Comedor", icon: "bulb", x: 720, y: 40, w: 340, h: 200,
      temp: 21.1, state: "off", devices: 3,
      pings: [{ x: 80, y: 90 }, { x: 220, y: 120 }, { x: 280, y: 80 }] },
    { id: "dormi", name: "Dormitorio", icon: "bulb", x: 40, y: 340, w: 380, h: 240,
      temp: 19.6, state: "off", devices: 4,
      pings: [{ x: 90, y: 120 }, { x: 220, y: 80 }, { x: 340, y: 160 }, { x: 160, y: 200 }] },
    { id: "bano", name: "Baño", icon: "wifi", x: 440, y: 260, w: 200, h: 160,
      temp: 22.0, state: "off", devices: 2,
      pings: [{ x: 80, y: 60 }, { x: 130, y: 120 }] },
    { id: "estudio", name: "Estudio", icon: "music", x: 660, y: 260, w: 200, h: 160,
      temp: 22.4, state: "on", devices: 4,
      pings: [{ x: 60, y: 60 }, { x: 150, y: 90 }, { x: 90, y: 120 }, { x: 160, y: 40 }] },
    { id: "patio", name: "Patio", icon: "energy", x: 880, y: 260, w: 180, h: 320,
      temp: 16.0, state: "off", devices: 2,
      pings: [{ x: 60, y: 120 }, { x: 120, y: 260 }] },
    { id: "entrada", name: "Entrada", icon: "shield", x: 660, y: 440, w: 200, h: 140,
      temp: null, state: "off", devices: 3,
      pings: [{ x: 50, y: 60 }, { x: 160, y: 90 }, { x: 100, y: 50 }] },
  ] satisfies readonly Room[],
  /**
   * Soft hallway connections drawn as wide low-opacity lines between
   * room centres. Order is incidental; each segment is symmetric.
   */
  links: [
    ["living", "cocina"],
    ["cocina", "comedor"],
    ["living", "dormi"],
    ["cocina", "bano"],
    ["comedor", "estudio"],
    ["estudio", "patio"],
    ["estudio", "entrada"],
    ["bano", "estudio"],
  ] as const,
} as const;

function roomCentre(room: Room): { x: number; y: number } {
  return { x: room.x + room.w / 2, y: room.y + room.h / 2 };
}

export function HomeGraph(): JSX.Element {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>("living");

  const { width, height, rooms, links } = FLOORPLAN;

  return (
    <section
      className={styles.card}
      data-testid="home-graph"
      aria-label={t("dashboard.home_graph.section_label")}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>{t("dashboard.home_graph.title")}</h2>
        <p className={styles.subtitle}>
          {t("dashboard.home_graph.subtitle", { count: rooms.length })}
        </p>
      </header>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className={styles.svg}
        role="img"
        aria-labelledby="home-graph-svg-title"
      >
        <title id="home-graph-svg-title">
          {t("dashboard.home_graph.svg_aria_title")}
        </title>

        {/* Outer wall — page-bg-toned rect with subtle stroke. */}
        <rect
          x={20}
          y={20}
          width={width - 40}
          height={height - 40}
          rx={28}
          ry={28}
          className={styles.outerWall}
        />

        {/* Hallway lines between connected rooms. */}
        {links.map(([aId, bId]) => {
          const a = rooms.find((r) => r.id === aId);
          const b = rooms.find((r) => r.id === bId);
          if (!a || !b) return null;
          const ac = roomCentre(a);
          const bc = roomCentre(b);
          return (
            <line
              key={`${aId}-${bId}`}
              x1={ac.x}
              y1={ac.y}
              x2={bc.x}
              y2={bc.y}
              className={styles.hallway}
            />
          );
        })}

        {/* Rooms */}
        {rooms.map((room) => {
          const isSelected = room.id === selected;
          const isOn = room.state === "on";
          const groupClass = [
            styles.room,
            isOn ? styles.roomOn : null,
            isSelected ? styles.roomSelected : null,
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <g
              key={room.id}
              className={groupClass}
              onClick={() => setSelected(room.id)}
              data-testid={`home-graph-room-${room.id}`}
            >
              <rect
                x={room.x}
                y={room.y}
                width={room.w}
                height={room.h}
                rx={20}
                ry={20}
                className={styles.roomRect}
              />

              {/* Animated device dots. Pulse only when the room is on. */}
              {room.pings.map((p, i) => {
                const cx = room.x + p.x;
                const cy = room.y + p.y;
                const r = isOn ? 4 : 3;
                return (
                  <circle
                    key={`${room.id}-ping-${i}`}
                    cx={cx}
                    cy={cy}
                    r={r}
                    className={isOn ? styles.pingOn : styles.pingOff}
                  >
                    {isOn && (
                      <animate
                        attributeName="opacity"
                        values="0.55;1;0.55"
                        dur={`${2 + (i % 3) * 0.7}s`}
                        begin={`${i * 0.4}s`}
                        repeatCount="indefinite"
                      />
                    )}
                  </circle>
                );
              })}

              {/* Label block (HTML inside foreignObject so we get the
                  text shaping we'd get in a real DOM element). */}
              <foreignObject
                x={room.x + 16}
                y={room.y + 14}
                width={room.w - 32}
                height={68}
              >
                <div className={styles.labelBox}>
                  <div className={styles.labelRow}>
                    <Icon name={room.icon} size={14} />
                    <span className={styles.labelName}>{room.name}</span>
                    {isOn && <span className={styles.activeDot} aria-hidden="true" />}
                  </div>
                  <div className={styles.labelMeta}>
                    {t("dashboard.home_graph.room_meta", {
                      count: room.devices,
                      temp: room.temp !== null ? `${room.temp}°` : "—",
                    })}
                  </div>
                </div>
              </foreignObject>
            </g>
          );
        })}

        {/* North compass marker — decorative chrome. */}
        <g
          transform={`translate(${width - 70}, 56)`}
          className={styles.compass}
          aria-hidden="true"
        >
          <circle r={20} fill="none" />
          <path d="M 0,-12 L 4,4 L 0,1 L -4,4 Z" />
          <text x={0} y={-26} textAnchor="middle" className={styles.compassN}>
            N
          </text>
        </g>
      </svg>
    </section>
  );
}
